import React, { useEffect, useState } from 'react';
import { AlertTriangle, MapPin, Calendar, RefreshCw, BellOff, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

/**
 * Historical view of unauthorised-exit + other high-priority alerts.
 * Visibility: alerts about anyone in the viewer's reporting tree
 * (admins → all staff; managers → down-line; staff → themselves only).
 *
 * ``compact`` = mobile-style stacked cards (used inside the staff bottom-nav).
 */
export default function AlertCenter({ compact = false }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = date ? `${API_URL}/api/alerts/history?date=${date}` : `${API_URL}/api/alerts/history`;
      const r = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (r.ok) setRows(await r.json());
    } finally { setLoading(false); }
  };

  const handleExport = async () => {
    const token = localStorage.getItem('token');
    const url = date ? `${API_URL}/api/alerts/history/export?date=${date}` : `${API_URL}/api/alerts/history/export`;
    const r = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
    if (!r.ok) return;
    const blob = await r.blob();
    const dl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = dl;
    a.download = `carloo_alerts_${date || today}.xlsx`;
    a.click();
    URL.revokeObjectURL(dl);
  };

  useEffect(() => { fetchData(); }, [date]);

  return (
    <div className={compact ? 'space-y-3' : 'space-y-4'} data-testid="alert-center">
      {/* Header */}
      <div className={`bg-white ${compact ? 'rounded-xl p-3' : 'rounded-xl p-4'} shadow-sm flex items-center justify-between gap-3`}>
        <div className="flex items-center gap-2">
          <AlertTriangle className="text-red-500" size={compact ? 16 : 20} />
          <div>
            <h3 className={compact ? 'font-semibold text-sm' : 'font-bold text-lg'}>Alert Center</h3>
            <p className="text-[11px] text-gray-500">Unauthorised exits + high-priority events</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchData}
            className="p-2 bg-gray-100 hover:bg-gray-200 rounded"
            data-testid="alert-center-refresh"
            title="Refresh"
          ><RefreshCw size={14} className={loading ? 'animate-spin' : ''} /></button>
          <button
            onClick={handleExport}
            disabled={rows.length === 0}
            className="inline-flex items-center gap-1 px-2.5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 disabled:cursor-not-allowed text-white rounded text-xs font-semibold"
            data-testid="alert-center-export"
            title="Download Excel"
          ><Download size={12} /> Excel</button>
        </div>
      </div>

      {/* Date filter */}
      <div className="bg-white rounded-xl p-3 shadow-sm flex items-center gap-2 text-sm">
        <Calendar size={14} className="text-gray-500" />
        <span className="text-xs text-gray-500">Date:</span>
        <input
          type="date" value={date} max={today}
          onChange={(e) => setDate(e.target.value)}
          className="flex-1 px-2 py-1 border rounded text-sm"
          data-testid="alert-center-date"
        />
        {date && (
          <button
            onClick={() => setDate('')}
            className="text-xs text-blue-600 hover:underline"
            data-testid="alert-center-clear-date"
          >Clear</button>
        )}
      </div>

      {/* List */}
      {loading ? (
        <p className="bg-white rounded-xl p-6 text-center text-sm text-gray-500 shadow-sm">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-xl p-8 text-center shadow-sm">
          <BellOff size={32} className="mx-auto text-gray-300 mb-2" />
          <p className="text-sm text-gray-500">
            {date ? `No alerts on ${date}.` : 'No alerts yet — your team is on track.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm divide-y divide-gray-100 overflow-hidden">
          {rows.map(a => {
            const dur = a.data?.duration_minutes;
            const staffName = a.data?.staff_name || '—';
            const map = a.data?.map_link;
            return (
              <div key={a.id} className="px-4 py-3" data-testid={`alert-row-${a.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2 min-w-0 flex-1">
                    <span className={`inline-flex w-2 h-2 rounded-full mt-2 flex-shrink-0 ${a.any_unread ? 'bg-red-500 animate-pulse' : 'bg-gray-300'}`} />
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-sm">{staffName}</p>
                      <p className="text-xs text-gray-600 mt-0.5">
                        {a.category === 'unauthorised_exit' ? 'Unauthorised exit' : a.title}
                        {dur != null && ` · ${Math.round(dur)} min`}
                      </p>
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        {fmtDateTime(a.created_at)}
                        {a.recipient_names?.length > 0 && ` · alerted: ${a.recipient_names.join(', ')}`}
                      </p>
                    </div>
                  </div>
                  {map && (
                    <a
                      href={map}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline whitespace-nowrap flex-shrink-0"
                      data-testid={`alert-row-${a.id}-map`}
                    ><MapPin size={11} /> Map</a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
